/**
 * Customer search for the quote builder — `GET /countries/:cc/parties?q=`,
 * spoken through the vendored `Combobox` (WEB TIER A · WT6; ledger item 14's
 * async enabler is what made a SERVER search expressible there).
 *
 * ===========================================================================
 * THE CREATE ACTION IS A ROUTE NOW, NOT A REFUSAL
 * ===========================================================================
 * This picker used to search-and-select only, pointing an empty result at the
 * inquiry panel's inline prospect form — a destination WT5 deleted (the link
 * was `?panel=new`, a retired URL param: a dead link this migration also
 * fixes). The forms.html ruling replaced that shape everywhere: "they are not
 * in the list" is a ROUTE — the Combobox's footer row leaves for
 * `/countries/:cc/parties/new` carrying `{ returnTo, name, draft }`, and that
 * page returns with `{ party, draft }` — the customer chosen and the builder
 * still filled in. The hand-off itself is the CALLER's (`onCreateProspect`):
 * only the builder knows its own draft.
 *
 * The search is unchanged underneath: same route, same `perPage: 8` cap, same
 * two-character floor as the inquiry page's picker — this is a picker, not a
 * list. `serverFiltered` is load-bearing: the rows ARE the server's answer to
 * the query, and cmdk re-filtering them would hide a match made on a column
 * the client does not render.
 */

import { useState } from 'react';
import { Button, Checkbox, Combobox, CountryBadge } from '../../components/ui';
import { usePartyLookup } from './use-quotes';
import type { PartyDto } from './api';
import { customerOriginLabel } from '../../domain/countries';
import type { CountryCode } from '../../domain/countries';

export function PartyPicker({
  countryCode,
  party,
  onSelect,
  onClear,
  onCreateProspect,
}: {
  countryCode: CountryCode;
  party: PartyDto | null;
  onSelect: (party: PartyDto) => void;
  onClear: () => void;
  /** The create-from-what-you-typed hand-off — the caller routes to
   * `parties/new` with its own draft (header). Receives the trimmed query.
   * OPTIONAL because only the callers that can restore a draft may offer it:
   * the quote builder wires it; `CrfCreatePage` (the other consumer) has no
   * round-trip contract yet and simply gets no create row — the same
   * search-and-select surface it always had. */
  onCreateProspect?: (typedName: string) => void;
}): React.JSX.Element {
  const [term, setTerm] = useState('');
  /*
   * AM-1 (v24 A1.4) — the picker's ruled default EXCLUDES `minted_unmatched`
   * prospects (importer-minted so no history was skipped, R10-A — not real
   * pickable customers until someone means to). The toggle below is the "same
   * visible toggle" the ruling names: plainly visible state, sent on every
   * request as include_history=0/1, never a hidden preference.
   */
  const [includeHistory, setIncludeHistory] = useState(false);
  const search = usePartyLookup(countryCode, term, party === null, includeHistory);

  if (party !== null) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-sm border border-line bg-subtle px-3 py-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-body font-medium text-fg">{party.name}</span>
          {/* PHASE 3a — the chosen customer's country, on its metadata line. */}
          <span className="flex min-w-0 items-center gap-1.5">
            <CountryBadge code={countryCode} />
            <span className="min-w-0 truncate text-label text-fg-secondary">
              {party.source === 'prospect' ? 'Prospect' : 'Tally customer'}
              {party.city === null ? '' : ` · ${party.city}`}
              {/*
                * §B3 — WHERE THE CUSTOMER IS, when that is not the selling country.
                *
                * The badge to the left is the SCOPE raising the quote. For the five
                * domestic countries the two are the same and this adds nothing, so it
                * renders nothing. For XM they differ, and this is the only text on the
                * card that says whether this is a Kenyan or an Omani customer.
                */}
              {customerOriginLabel(party.customer_country) === null
                ? ''
                : ` · ${customerOriginLabel(party.customer_country) ?? ''}`}
            </span>
          </span>
        </div>
        <Button variant="ghost" onClick={onClear}>
          Change
        </Button>
      </div>
    );
  }

  const results = search.data?.data ?? [];
  const searched = term.trim().length >= 2;
  /* The whole state ladder speaks through the Combobox's one honest empty
   * slot — the same sentences the pre-migration picker rendered as rows. */
  const emptyText = !searched
    ? 'Type at least two characters.'
    : search.isPending
      ? 'Searching…'
      : search.isError
        ? 'The customer list could not be searched. The Parties permission governs this route.'
        : `No customer in ${countryCode} matches that.`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor="quote-party-picker" className="text-label font-medium text-fg">
        Customer
      </label>
      <Combobox
        id="quote-party-picker"
        options={results.map((candidate) => ({
          value: candidate.id,
          label: candidate.name,
          sub: `${candidate.source === 'prospect' ? 'Prospect' : 'Tally customer'}${
            candidate.city === null ? '' : ` · ${candidate.city}`
          }${
            customerOriginLabel(candidate.customer_country) === null
              ? ''
              : ` · ${customerOriginLabel(candidate.customer_country) ?? ''}`
          }${
            /* AM-1: a minted prospect that DOES arrive (toggle on, or a future
             * server widening) says what it is — never a silent lookalike. */
            candidate.legacy_origin === 'minted_unmatched' ? ' · legacy history' : ''
          }`,
        }))}
        value={null}
        onSelect={(id) => {
          const candidate = results.find((row) => row.id === id);
          if (candidate !== undefined) {
            onSelect(candidate);
            setTerm('');
          }
        }}
        listLabel="Customer"
        placeholder="Search customers by name"
        searchPlaceholder="Search customers and prospects"
        emptyText={emptyText}
        serverFiltered
        onQueryChange={setTerm}
        onCreate={
          onCreateProspect === undefined
            ? undefined
            : (query) => onCreateProspect(query.trim())
        }
        createLabel={(query) =>
          query.trim() === ''
            ? 'Create a customer on the New customer page…'
            : `Create “${query.trim()}” on the New customer page…`
        }
      />
      {/* AM-1's one visible toggle — the ruled words are "plainly visible state,
          never a hidden preference", so it sits in the layout, not in a menu. */}
      <Checkbox
        label="Include history"
        checked={includeHistory}
        onChange={(event) => setIncludeHistory(event.target.checked)}
      />
      {onCreateProspect === undefined ? null : (
        <p className="text-label text-fg-secondary">
          Someone not in the list is created on the New customer page — this builder returns
          filled in, with them chosen.
        </p>
      )}
    </div>
  );
}
